import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePermissions } from '@/api/permissions';
import { canOpen } from '@/lib/permissions';
import { ROUTES } from '@/lib/routes';
import { MD_UP, useMediaQuery } from '@/lib/useMediaQuery';

/**
 * Phones: a page with no phone layout. The route guard shows `DesktopNotice` before the page mounts (pages are listed
 * in `DESKTOP_ONLY`, navigation.ts); this wrapper is the fallback inside pages and never mounts its children on a
 * phone (it used to hide them with CSS, so the page still rendered and fetched). "Open anyway" lasts for the tab.
 */
const KEY = 'cosmos:open-anyway';
const openedAnyway = (path: string) => { try { return (JSON.parse(sessionStorage.getItem(KEY) ?? '[]') as string[]).includes(path); } catch { return false; } };
const rememberOpenAnyway = (path: string) => { try { sessionStorage.setItem(KEY, JSON.stringify([...new Set([...(JSON.parse(sessionStorage.getItem(KEY) ?? '[]') as string[]), path])])); } catch { /* storage unavailable */ } };

/** Whether a desktop-only page should render here (desktop, or the user chose "Open anyway"). */
export function useShowDesktopPage(): [boolean, () => void] {
  const { pathname } = useLocation();
  const isDesktop = useMediaQuery(MD_UP);
  // The path chosen, not a boolean: the route guard stays mounted across navigation.
  const [anyway, setAnyway] = useState<string | null>(null);
  return [isDesktop || anyway === pathname || openedAnyway(pathname), () => { rememberOpenAnyway(pathname); setAnyway(pathname); }];
}

export function DesktopNotice({ area, onOpenAnyway }: { area: string; onOpenAnyway: () => void }) {
  const perms = usePermissions();
  // The nearest page that works on a phone.
  const alt = [{ to: ROUTES.insights.find, label: 'Find a device or person' }, { to: ROUTES.psa.tickets.mine, label: 'My tickets' }]
    .find((a) => canOpen(perms, a.to));
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 text-center">
      <Monitor className="h-10 w-10 text-muted-foreground" />
      <div className="text-sm font-medium text-foreground">{area} is available on desktop</div>
      <p className="max-w-xs text-xs text-muted-foreground">This page doesn't have a phone layout yet. Open Cosmos on a larger screen, or open it here anyway.</p>
      <div className="flex flex-wrap justify-center gap-2">
        {alt && <Button asChild size="sm"><Link to={alt.to}>{alt.label}</Link></Button>}
        <Button size="sm" variant="outline" onClick={onOpenAnyway}>Open anyway</Button>
      </div>
    </div>
  );
}

export function DesktopOnly({ children, title }: { children: React.ReactNode; title?: string; message?: string }) {
  const [show, openAnyway] = useShowDesktopPage();
  if (show) return <>{children}</>;
  return <DesktopNotice area={(title ?? 'This page').replace(/ (is|are) available on desktop$/, '')} onOpenAnyway={openAnyway} />;
}
